import {
  useMutation as useTanstackMutation,
  useQuery as useTanstackQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
  OptionalRestArgs,
} from "convex/server";
import { ConvexError } from "convex/values";
import { useCallback, type ReactNode } from "react";
import {
  clearImpersonationSession,
  impersonationHeaders,
} from "./impersonation";
import { useAuth } from "./local-auth";
import { readRpcEnvelope } from "./rpc-response";
import { rpcPath } from "./rpc-reference";
import { publishRpcMutation } from "./rpc-sync";
import { reportClientDebugEvent } from "./client-observability";
import { createRequestLimiter } from "./request-limiter";
import { refreshRpcQueries } from "./rpc-refresh";

const acquireReadSlot = createRequestLimiter(3);

type QueryReference = FunctionReference<"query">;
type MutationReference = FunctionReference<"mutation">;
type ActionReference = FunctionReference<"action">;

type QueryArgs<Query extends QueryReference> =
  | [args: FunctionArgs<Query>]
  | [args: "skip"];

export type LocalQueryState<Return> = {
  data: Return | undefined;
  error: Error | null;
  isLoading: boolean;
  retry: () => void;
};

async function callRpc<Return>(
  kind: "query" | "mutation" | "action",
  reference: unknown,
  args: unknown,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Return> {
  const path = rpcPath(reference);
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort(options?.signal?.reason);
  if (options?.signal?.aborted) abort();
  else options?.signal?.addEventListener("abort", abort, { once: true });
  const timeout = options?.timeoutMs
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, options.timeoutMs)
    : undefined;
  let releaseRead: (() => void) | undefined;
  try {
    if (kind === "query") releaseRead = await acquireReadSlot(controller.signal);
    controller.signal.throwIfAborted();
    let response: Response;
    try {
      response = await fetch("/api/rpc", {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "content-type": "application/json",
          ...impersonationHeaders(),
        },
        body: JSON.stringify({
          kind,
          path,
          args: args ?? {},
        }),
      });
    } catch (error) {
      reportClientDebugEvent({
        event: "rpc.network_error",
        level: "error",
        details: { kind, path, error },
      });
      throw error;
    }

    const payload = await readRpcEnvelope<Return>(response);
    if (!response.ok || payload.error) {
      if (
        payload.error?.code === "IMPERSONATION_EXPIRED" ||
        payload.error?.code === "IMPERSONATION_TOKEN_INVALID" ||
        payload.error?.code === "IMPERSONATION_TARGET_INVALID"
      ) {
        clearImpersonationSession();
      }
      const errorData: Record<string, unknown> =
        payload.error?.data &&
        typeof payload.error.data === "object" &&
        !Array.isArray(payload.error.data)
          ? (payload.error.data as Record<string, unknown>)
          : {};
      const data = {
        ...errorData,
        code: payload.error?.code ?? "INTERNAL_ERROR",
        message: payload.error?.message ?? "Errore del server",
      };
      reportClientDebugEvent({
        event: "rpc.error",
        level: response.status >= 500 ? "error" : "warn",
        details: {
          kind,
          path,
          status: response.status,
          code: data.code,
          requestId:
            typeof errorData.requestId === "string"
              ? errorData.requestId
              : undefined,
          message: data.message,
        },
      });
      throw new ConvexError(data as never);
    }
    return payload.data as Return;
  } catch (error) {
    if (timedOut) {
      throw new ConvexError({
        code: "REQUEST_TIMEOUT",
        message:
          "Il caricamento sta impiegando troppo tempo. Riprova tra qualche istante.",
      });
    }
    throw error;
  } finally {
    releaseRead?.();
    if (timeout !== undefined) clearTimeout(timeout);
    options?.signal?.removeEventListener("abort", abort);
  }
}

import { crmRefreshInterval } from "./crm-refresh-interval";

function queryKey(reference: unknown, args: unknown) {
  return ["local-rpc", rpcPath(reference), args] as const;
}

function useLocalQueryState<Query extends QueryReference>(
  query: Query,
  input: QueryArgs<Query>[0],
): LocalQueryState<FunctionReturnType<Query>> {
  const result = useTanstackQuery({
    queryKey: queryKey(query, input === "skip" ? null : input),
    queryFn: ({ signal }) =>
      callRpc<FunctionReturnType<Query>>("query", query, input ?? {}, {
        signal,
        timeoutMs: 30_000,
      }),
    enabled: input !== "skip",
    staleTime: rpcPath(query) === "crm/notifications:listMine" ? 0 : 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: state => crmRefreshInterval(rpcPath(query), state.state.data),
    refetchIntervalInBackground: false,
    retry: false,
  });
  return {
    data: input === "skip" ? undefined : result.data,
    error:
      input === "skip"
        ? null
        : result.error instanceof Error
          ? result.error
          : null,
    isLoading: input !== "skip" && result.isPending,
    retry: () => {
      void result.refetch();
    },
  };
}

export function useQuery<Query extends QueryReference>(
  query: Query,
  ...args: QueryArgs<Query>
): FunctionReturnType<Query> | undefined {
  return useLocalQueryState(query, args[0]).data;
}

export function useQueryState<Query extends QueryReference>(
  query: Query,
  ...args: QueryArgs<Query>
): LocalQueryState<FunctionReturnType<Query>> {
  return useLocalQueryState(query, args[0]);
}

type LocalMutation<Mutation extends MutationReference> = (
  ...args: OptionalRestArgs<Mutation>
) => Promise<FunctionReturnType<Mutation>>;

export function useMutation<Mutation extends MutationReference>(
  mutation: Mutation,
): LocalMutation<Mutation> {
  const queryClient = useQueryClient();
  const { mutateAsync } = useTanstackMutation({
    mutationFn: (args: FunctionArgs<Mutation>) =>
      callRpc<FunctionReturnType<Mutation>>("mutation", mutation, args),
    onSuccess: () => {
      refreshRpcQueries(queryClient);
      publishRpcMutation();
    },
  });
  return useCallback(
    (args?: FunctionArgs<Mutation>) =>
      mutateAsync((args ?? {}) as FunctionArgs<Mutation>),
    [mutateAsync],
  ) as LocalMutation<Mutation>;
}

type LocalAction<Action extends ActionReference> = (
  ...args: OptionalRestArgs<Action>
) => Promise<FunctionReturnType<Action>>;

/** Read-only actions do not invalidate or broadcast changes to CRM queries. */
export function useReadAction<Action extends ActionReference>(
  action: Action,
): LocalAction<Action> {
  const path = rpcPath(action);
  return useCallback(
    (args?: FunctionArgs<Action>) =>
      callRpc<FunctionReturnType<Action>>(
        "action",
        { __rpcPath: path },
        args ?? {},
        {
          timeoutMs: 60_000,
        },
      ),
    [path],
  ) as LocalAction<Action>;
}

export function useAction<Action extends ActionReference>(
  action: Action,
): LocalAction<Action> {
  const queryClient = useQueryClient();
  const { mutateAsync } = useTanstackMutation({
    mutationFn: (args: FunctionArgs<Action>) =>
      callRpc<FunctionReturnType<Action>>("action", action, args),
    onSuccess: () => {
      refreshRpcQueries(queryClient);
      publishRpcMutation();
    },
  });
  return useCallback(
    (args?: FunctionArgs<Action>) =>
      mutateAsync((args ?? {}) as FunctionArgs<Action>),
    [mutateAsync],
  ) as LocalAction<Action>;
}

export function useConvexAuth() {
  const auth = useAuth();
  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
  };
}

export function Authenticated({ children }: { children: ReactNode }) {
  return useAuth().isAuthenticated ? <>{children}</> : null;
}

export function Unauthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  return !isLoading && !isAuthenticated ? <>{children}</> : null;
}

export function AuthLoading({ children }: { children: ReactNode }) {
  return useAuth().isLoading ? <>{children}</> : null;
}

export class ConvexReactClient {
  constructor(_url?: string) {}
}

export function ConvexProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
